/**
 * A tela de Relatórios (11.10) — a casca das abas, com a aba na URL (`?aba=`).
 *
 * A rota é fina: resolve a aba da busca e a passa à casca (`modulos/relatorios/Relatorios.tsx`),
 * que não importa a rota (evita o ciclo). **Nenhuma chamada de serviço aqui**, e a ausência mantém
 * este arquivo fora da varredura de telas transacionais de `src/api/cliente.test.ts`.
 *
 * O título é publicado pela rota: o item de menu não existe para a SDR, e quem chega por link
 * direto ainda precisa do `<h1>` certo (a tela diz que ela não tem acesso).
 */

import { createRoute } from '@tanstack/react-router'
import { Suspense, lazy } from 'react'

import type { AbaComDados } from '@/modulos/relatorios/api'
import { ABA_PADRAO, abaDaChave, declaracaoDa } from '@/modulos/relatorios/filtros'
import { useDefinirTitulo } from '@/shell/Shell'
import { CAMINHOS, TITULO_DOS_RELATORIOS } from '@/shell/navegacao'
import { rotaDaAplicacao } from './aplicacao'

/**
 * A casca vem em pedaço próprio do bundle: ela traz o Recharts (AD-29), e quem nunca abre
 * Relatórios — a SDR, as telas de trabalho — não paga por ele.
 */
const Relatorios = lazy(() =>
  import('@/modulos/relatorios/Relatorios').then((modulo) => ({ default: modulo.Relatorios })),
)

export interface BuscaDosRelatorios {
  aba?: string
}

function TelaDeRelatorios() {
  useDefinirTitulo(TITULO_DOS_RELATORIOS)
  const { aba }: BuscaDosRelatorios = rotaDeRelatorios.useSearch()
  const navegar = rotaDeRelatorios.useNavigate()

  function abrir(destino: AbaComDados) {
    void navegar({ search: destino === ABA_PADRAO ? {} : { aba: declaracaoDa(destino).chave } })
  }

  return (
    <Suspense fallback={<p className="relatorios__estado">Carregando …</p>}>
      <Relatorios aba={abaDaChave(aba)} aoAbrirAba={abrir} />
    </Suspense>
  )
}

export const rotaDeRelatorios = createRoute({
  getParentRoute: () => rotaDaAplicacao,
  path: CAMINHOS.relatorios,
  component: TelaDeRelatorios,
  /** A URL é entrada: aba desconhecida cai fora aqui, e a tela abre em Resultado do mês. */
  validateSearch: (parametros: Record<string, unknown>): BuscaDosRelatorios => {
    const aba = abaDaChave(parametros.aba)
    return aba === undefined || aba === ABA_PADRAO ? {} : { aba: declaracaoDa(aba).chave }
  },
})
